import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000' });

api.interceptors.request.use((config) => {
  const stored = localStorage.getItem('gsw_auth');
  if (stored) {
    const { token } = JSON.parse(stored);
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface LoginResponse {
  access_token: string;
  token_type: string;
  role: string;
  full_name: string;
}

export type StrategyState =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'OUTPUT_READY'
  | 'LIVE'
  | 'EDIT_REQUESTED'
  | 'UPLOAD_FAILED'
  | 'INACTIVE';

export interface IndexComponent {
  security_code: string;
  weight: number;
}

export interface RunResponse {
  success: boolean;
  strategy_state: StrategyState;
  errors: string[];
  index_output: IndexComponent[] | null;
}

export const PENDING_NAME = 'Pending Name Approval';

export interface Strategy {
  id: number;
  quant_id: number;
  name: string;
  suggested_name?: string | null;
  description: string | null;
  code: string | null;
  state: StrategyState;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  index_output: IndexComponent[] | null;
  allow_code_view: boolean;
  code_view_requested: boolean;
  edit_unlocked: boolean;
  edit_request_reason?: string | null;
  edit_request_at?: string | null;
  simulation_settings_saved_at: string | null;
  rebalance_cadence: string;
  change_mode: string;
  significance_threshold: number;
  expense_ratio_pct_pa: number;
}

export interface AdminStrategyRow {
  strategy_id: number;
  quant_name: string;
  quant_email: string;
  strategy_name: string;
  submission_date: string | null;
  eod_data_date: string | null;
  state: StrategyState;
  allow_code_view: boolean;
  code_view_requested: boolean;
  has_csv: boolean;
  edit_unlocked: boolean;
  suggested_name: string | null;
  model_id: string | null;
  last_checked_at: string | null;
  edit_request_reason: string | null;
  edit_request_at: string | null;
  rebalance_cadence: string;
  change_mode: string;
  significance_threshold: number;
  last_run_status: string | null;
  last_run_error: string | null;
}

export interface StrategyVersionRow {
  id: number;
  version_number: number;
  run_date: string | null;
  eod_data_date: string | null;
  security_count: number;
  has_csv: boolean;
  meaningful_change: boolean | null;
}

export interface RerunResponse {
  success: boolean;
  strategy: AdminStrategyRow;
  errors: string[];
  version_number: number | null;
  meaningful_change?: boolean;
}

export interface StrategyConfigResponse {
  allow_multiple_strategies: boolean;
}

export interface EodLatestResponse {
  date: string | null;
  security_count: number;
  fundamentals_count: number;
  sectors_loaded: boolean;
}

export interface EodStatusResponse {
  latest_db_date: string | null;
  latest_folder_date: string | null;
  price_days: number;
  symbol_count: number;
  fundamentals_count: number;
  large_mid_cap_count: number;
  today_files_received: boolean;
}

export interface IsinSyncResult {
  updated: number;
}

export interface LoaderResult {
  file?: string | null;
  rows?: number | null;
  inserted?: number | null;
  updated?: number | null;
  skipped?: number | null;
  error?: string | null;
  db_rows?: number | null;
}

export interface SyncAllResult {
  xm:             LoaderResult;
  we:             LoaderResult;
  we_history:     LoaderResult;
  wy:             LoaderResult;
  xc:             LoaderResult;
  wfp:            LoaderResult;
  company_master: LoaderResult;
  nav:            LoaderResult;
}

export interface IndexValuePoint {
  date: string;
  index_value: number;
  daily_return_pct: number | null;
  invested_pct: number;
  fee_drag: number;
  txn_cost_drag: number;
  turnover_pct: number;
  rebalance_signal: boolean;
}

export interface IndexValuesResponse {
  index: IndexValuePoint[];
  portfolio: IndexValuePoint[];
}

export interface IndexSeriesLatest {
  date: string | null;
  index_value: number | null;
  daily_return_pct: number | null;
  invested_pct: number | null;
  rebalance_signal: boolean | null;
}

export interface IndexLatestResponse {
  index: IndexSeriesLatest;
  portfolio: IndexSeriesLatest;
  eod_data_date: string | null;
  implementation_shortfall_pct: number | null;
}

export interface IndexOverviewRow {
  strategy_id: number;
  strategy_name: string;
  portfolio_latest_value: number | null;
  portfolio_latest_date: string | null;
  index_latest_value: number | null;
  index_latest_date: string | null;
}

export interface IndexOverviewResponse {
  system_latest_eod_date: string | null;
  strategies: IndexOverviewRow[];
}

export interface PlatformSettingRow {
  id: number;
  key: string;
  value: number;
  effective_from: string;
  created_at: string;
  created_by_user_id: number | null;
  note: string | null;
}

export interface PlatformSettingHistory {
  key: string;
  current_value: number | null;
  current_effective_from: string | null;
  history: PlatformSettingRow[];
}

export interface AuditLogEntry {
  id: number;
  strategy_id: number;
  strategy_name: string;
  quant_name: string;
  old_state: string | null;
  new_state: string;
  actor: string;
  created_at: string;
}

export interface AdminUser {
  id: number;
  email: string;
  full_name: string;
  role: string;
  created_at: string;
  is_active: boolean;
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { email, password }),
  microsoftToken: (code: string, codeVerifier: string) =>
    api.post<LoginResponse>('/auth/microsoft/token', { code, code_verifier: codeVerifier }),
};

export const strategyApi = {
  getAll: () => api.get<Strategy[]>('/strategies/'),
  getMine: () => api.get<Strategy>('/strategies/mine'),
  getConfig: () => api.get<StrategyConfigResponse>('/strategies/config'),
  create: (data: { suggested_name: string; description?: string; code?: string }) =>
    api.post<Strategy>('/strategies/', data),
  update: (id: number, data: { name?: string; description?: string; code?: string; rebalance_cadence?: string; change_mode?: string; significance_threshold?: number; expense_ratio_pct_pa?: number }) =>
    api.put<Strategy>(`/strategies/${id}`, data),
  submit: (id: number) => api.post<Strategy>(`/strategies/${id}/submit`),
  requestEdit: (id: number, reason: string) =>
    api.post<Strategy>(`/strategies/${id}/request-edit`, { reason }),
  reset: (id: number) => api.delete<Strategy>(`/strategies/${id}/reset`),
  setCodeVisibility: (id: number, allow_code_view: boolean) =>
    api.patch<Strategy>(`/strategies/${id}/code-visibility`, { allow_code_view }),
};

export const executionApi = {
  run: (strategyId: number) =>
    api.post<RunResponse>(`/api/execution/run/${strategyId}`),
};

export const eodApi = {
  getLatest: () => api.get<EodLatestResponse>('/api/eod/latest'),
  getStatus: () => api.get<EodStatusResponse>('/api/eod/status'),
  resolveNames: (codes: string[]) =>
    api.post<{ names: Record<string, string> }>('/api/eod/resolve-names', { codes }),
};

export const adminApi = {
  getAllStrategies: () => api.get<AdminStrategyRow[]>('/admin/strategies'),
  updateStrategyState: (id: number, state: StrategyState) =>
    api.put<AdminStrategyRow>(`/admin/strategies/${id}/state`, { state }),
  markLive: (id: number) =>
    api.put<AdminStrategyRow>(`/admin/strategies/${id}/mark-live`),
  setModelId: (id: number, model_id: string) =>
    api.patch<AdminStrategyRow>(`/admin/strategies/${id}/model-id`, { model_id }),
  toggleEdit: (id: number) =>
    api.patch<AdminStrategyRow>(`/admin/strategies/${id}/toggle-edit`),
  downloadCsv: (id: number) =>
    api.get(`/admin/strategies/${id}/download-csv`, { responseType: 'blob' }),
  getAuditLog: () => api.get<AuditLogEntry[]>('/admin/audit-log'),
  regenerateCsv: (id: number) =>
    api.post<AdminStrategyRow>(`/admin/strategies/${id}/regenerate-csv`),
  getCode: (id: number) =>
    api.get<{ code: string }>(`/admin/strategies/${id}/code`),
  updateCode: (id: number, code: string) =>
    api.put<{ code: string }>(`/admin/strategies/${id}/code`, { code }),
  requestCodeView: (id: number) =>
    api.post<AdminStrategyRow>(`/admin/strategies/${id}/request-code-view`),
  syncIsinsFromFile: () =>
    api.post<IsinSyncResult>('/admin/sync-isins-from-file'),
  syncAll: () =>
    api.post<SyncAllResult>('/admin/sync-all'),
  rerun: (id: number) =>
    api.put<RerunResponse>(`/admin/strategies/${id}/rerun`),
  setName: (id: number, name: string) =>
    api.patch<AdminStrategyRow>(`/admin/strategies/${id}/set-name`, { name }),
  approveEdit: (id: number) =>
    api.post<AdminStrategyRow>(`/admin/strategies/${id}/approve-edit`),
  rejectEdit: (id: number) =>
    api.post<AdminStrategyRow>(`/admin/strategies/${id}/reject-edit`),
  markInactive: (id: number) =>
    api.put<AdminStrategyRow>(`/admin/strategies/${id}/mark-inactive`),
  deleteStrategy: (id: number) =>
    api.delete(`/admin/strategies/${id}`),
  getHistory: (id: number) =>
    api.get<StrategyVersionRow[]>(`/admin/strategies/${id}/history`),
  downloadVersionCsv: (strategyId: number, versionId: number) =>
    api.get(`/admin/strategies/${strategyId}/history/${versionId}/download-csv`, { responseType: 'blob' }),
  getUsers: () =>
    api.get<AdminUser[]>('/admin/users'),
  createUser: (data: { email: string; full_name: string; role: string }) =>
    api.post<AdminUser>('/admin/users', data),
  getIndexOverview: () =>
    api.get<IndexOverviewResponse>('/admin/index/overview'),
  getSettingHistory: (key: string) =>
    api.get<PlatformSettingHistory>(`/admin/settings/${key}`),
  createSetting: (key: string, data: { value: number; effective_from: string; note?: string }) =>
    api.post<PlatformSettingRow>(`/admin/settings/${key}`, data),
};

export const indexApi = {
  getValues: (strategyId: number) =>
    api.get<IndexValuesResponse>(`/index/${strategyId}/values`),
  getLatest: (strategyId: number) =>
    api.get<IndexLatestResponse>(`/index/${strategyId}/latest`),
};

export default api;
